import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useMemo } from 'react'
import { EmphasizedNumber, Icons, SkeletonText } from 'utoniq-ui'

import {
  Collection,
  EventPickupUsersOptions,
  Maybe,
  PickupUserBoxFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { SearchForm } from './SearchForm'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  type: string
  collectionId: string
  eventId: string
  collection: Maybe<Pick<Collection, 'id' | 'title'>>
  eventPickupUsers: {
    formattedPickupStartEndTime?: string | null
    pickupUsers: PickupUserBoxFragment[]
  }[]
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onRefresh: (values: EventPickupUsersOptions) => Promise<void>
}

export const ConsoleTicketReceivesTemplate: Template<State, Action> = ({
  state,
  onRefresh,
  onFetchMore,
}) => {
  const { t } = useTranslation('consoleTicketReceives')
  const { routerNames, routerPages, generatePath } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleTicketReceives} />
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          title: t('title'),
          right: !state.type && (
            <Button
              variant="text"
              sx={{ mr: -1, p: 0, width: 'max-content' }}
              href={generatePath(routerPages.ConsoleTicketReceives, {
                type: 'history',
                eventId: state.eventId,
                collectionId: state.collectionId,
              })}
            >
              <Typography variant="subtitle1" sx={{ color: 'white' }}>
                {t('pastReceiptManagement')}
              </Typography>
            </Button>
          ),
        }}
      >
        <Stack sx={{ py: 4 }}>
          {state.loading && !state.collection ? (
            <SkeletonText variant="h3" width="75%" />
          ) : (
            <Typography variant="h3">{state.collection?.title}</Typography>
          )}
        </Stack>
        <SearchForm artistId={state.artistId} onSubmit={onRefresh} />
        <ReceiveTimeCell
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          eventPickupUsers={state.eventPickupUsers}
          onFetchMore={onFetchMore}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type ReceiveTimeCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  eventPickupUsers: State['eventPickupUsers']
  onFetchMore: Action['onFetchMore']
}
const ReceiveTimeCell: Cell<ReceiveTimeCellProps> = ({
  loading,
  hasNextPage,
  onFetchMore,
  eventPickupUsers,
}) => {
  const { t } = useTranslation('consoleTicketReceives')
  return (
    <CellFrame>
      <ScrollFetcher
        nodes={eventPickupUsers.map(
          ({ pickupUsers, formattedPickupStartEndTime }) => (
            <ReceiveTimeItemCell
              pickupUsers={pickupUsers}
              formattedPickupStartEndTime={formattedPickupStartEndTime}
              key={formattedPickupStartEndTime}
            />
          )
        )}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 0 }}
        emptyMessage={t('receiveTimeEmptyMessage')}
        loadingComponent={<ReceiveTimeItemSkeleton />}
      />
    </CellFrame>
  )
}

type ReceiveTimeItemCellProps = {
  formattedPickupStartEndTime?: string | null
  pickupUsers: PickupUserBoxFragment[]
}
const ReceiveTimeItemCell: Cell<ReceiveTimeItemCellProps> = ({
  formattedPickupStartEndTime,
  pickupUsers,
}) => {
  const { t } = useTranslation('consoleTicketReceives')
  const { generatePath, routerPages } = useRouter()

  const isPaymentByPoint = useCallback((pickupUser: PickupUserBoxFragment) => {
    return pickupUser.cartTransactionOrders.some(
      value => value.paymentType === UserPaymentType.PrepaidPoint
    )
  }, [])

  return (
    <CellFrame>
      <Typography variant="subtitle1" sx={{ py: 4 }}>
        {t('pickupTime', { date: formattedPickupStartEndTime })}
      </Typography>
      <Stack spacing={2}>
        {pickupUsers.map((pickupUser, index) => (
          <Card elevation={0} sx={{ borderRadius: 1 }} key={index}>
            <CardActionArea
              href={generatePath(routerPages.ConsoleTicketReceiveDetail, {
                eventId: pickupUser.eventId,
                collectionId: pickupUser.collectionId,
                pickupUserId: pickupUser.id,
              })}
            >
              <CardContent sx={{ px: 4, py: 3, '&:last-child': { pb: 3 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <ExchangeCategory isUsed={!!pickupUser.isCompleted} />
                    <Stack direction="row" alignItems="center">
                      <Typography variant="h6">
                        {pickupUser.user?.fullName ??
                          pickupUser.user.displayName}
                      </Typography>
                      {!!pickupUser.user?.formattedPhoneNumber && (
                        <Typography variant="body2" color="text.secondary">
                          （{pickupUser.user?.formattedPhoneNumber}）
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {pickupUser.user?.email}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={4}>
                    <Stack flex={1}>
                      <Typography variant="caption" sx={styleSets.lineCamp(3)}>
                        {pickupUser.itemsString}
                      </Typography>
                    </Stack>
                    {isPaymentByPoint(pickupUser) ? (
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={1}
                      >
                        <Icons.Point fontSize="medium" />
                        <EmphasizedNumber variant="h5">
                          {pickupUser.formattedActualTotalAmount}
                        </EmphasizedNumber>
                      </Stack>
                    ) : (
                      <EmphasizedNumber variant="h5">
                        {t('price', {
                          price: pickupUser.formattedActualTotalAmount,
                        })}
                      </EmphasizedNumber>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </CellFrame>
  )
}

type ExchangeCategoryProps = {
  isUsed: boolean
}
export const ExchangeCategory: Cell<ExchangeCategoryProps> = ({ isUsed }) => {
  const { t } = useTranslation('consoleTicketReceives')

  const { bgcolor, text } = useMemo(() => {
    if (isUsed) return { bgcolor: 'grey.300', text: t('alreadyUsed') }
    return { bgcolor: 'primary.main', text: t('unused') }
  }, [isUsed, t])

  return (
    <Chip
      size="small"
      sx={{
        height: 20,
        borderRadius: '2px',
        bgcolor,
        py: '2px',
        '.MuiChip-label': { px: 1 },
      }}
      label={
        <Typography color="white" variant="overline">
          {text}
        </Typography>
      }
    />
  )
}

const ReceiveTimeItemSkeleton = () => (
  <Stack>
    <Stack sx={{ py: 4 }}>
      <SkeletonText variant="subtitle1" width="75%" />
    </Stack>
    <Stack spacing={2}>
      {Array.from(Array(3).keys()).map(key => (
        <Card elevation={0} key={key}>
          <CardContent sx={{ px: 4, py: 3, '&:last-child': { pb: 3 } }}>
            <Stack direction="row" alignItems="flex-end" spacing={4}>
              <Stack flex={3}>
                <Stack direction="row" spacing={2}>
                  <Stack flex={1}>
                    <SkeletonText variant="h3" />
                  </Stack>
                  <Stack flex={4}>
                    <SkeletonText variant="h3" />
                  </Stack>
                </Stack>
                <SkeletonText variant="small" numberOfLine={2} />
              </Stack>
              <Stack flex={1}>
                <SkeletonText variant="h3" />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  </Stack>
)
